import React, { useState, useEffect } from 'react';
import { getStudentCourseStatus } from '../services/courseService';
import { useTranslation } from 'react-i18next';

const CourseStatusIndicator = ({ courseId, onStatusChange, onBookingAction, promotedStudents = [], isGuardian = false }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    loadCourseStatus();
  }, [courseId]);

  useEffect(() => {
    if (status?.isPromoted && status?.promotionExpiry) {
      const timer = setInterval(() => {
        const now = new Date();
        const expiry = new Date(status.promotionExpiry);
        const diff = expiry - now;
        
        if (diff <= 0) {
          setTimeRemaining(null);
          // Reload status to see if promotion expired
          loadCourseStatus();
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          setTimeRemaining(`${hours}h ${minutes}m`);
        }
      }, 60000); // Update every minute

      return () => clearInterval(timer);
    }
  }, [status]);

  const loadCourseStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // For guardians with promoted students, override status
      if (isGuardian && promotedStudents.length > 0) {
        const promotedStudent = promotedStudents[0];
        const promotionStatus = {
          status: 'promoted',
          isPromoted: true,
          canBook: true,
          waitlistEntry: {
            studentName: promotedStudent.studentName,
            studentEmail: promotedStudent.studentEmail,
            promotionExpiry: promotedStudent.promotionExpiry
          },
          promotionExpiry: promotedStudent.promotionExpiry
        };
        setStatus(promotionStatus);
        
        if (onStatusChange) {
          onStatusChange(promotionStatus);
        }
      } else {
        const courseStatus = await getStudentCourseStatus(courseId);
        setStatus(courseStatus);
        
        if (onStatusChange) {
          onStatusChange(courseStatus);
        }
      }
    } catch (err) {
      console.error('Failed to load course status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingClick = () => {
    if (onBookingAction) {
      onBookingAction(courseId, status);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-4 h-4 bg-gray-300 rounded animate-pulse"></div>
        <span className="text-sm text-gray-500">{t('course.checkingStatus')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600">
        {t('course.errors.statusCheckFailed')}
      </div>
    );
  }

  if (!status) return null;

  const renderStatusBadge = () => {
    switch (status.status) {
      case 'promoted':
        return (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-800">
                  🎉 {t('course.status.promoted.title')}
                </h3>
                {/* Show student name prominently for guardian bookings */}
                {status.waitlistEntry?.studentName && (
                  <p className="text-sm text-green-700 font-semibold mt-1">
                    Student: {status.waitlistEntry.studentName}
                  </p>
                )}
                <p className="text-sm text-green-700 mt-1">
                  {isGuardian && status.waitlistEntry?.studentName 
                    ? `${status.waitlistEntry.studentName} has been promoted! Complete booking within ${timeRemaining || '24 hours'}.`
                    : t('course.status.promoted.message', { time: timeRemaining || t('course.status.promoted.limitedTime') })}
                </p>
                {timeRemaining && (
                  <p className="text-xs text-green-600 mt-1">
                    {t('course.status.promoted.expires', { time: timeRemaining })}
                  </p>
                )}
                {/* Show multiple promoted students if applicable */}
                {isGuardian && promotedStudents.length > 1 && (
                  <p className="text-xs text-green-600 mt-2">
                    Note: {promotedStudents.length} students promoted for this course
                  </p>
                )}
                <button
                  onClick={handleBookingClick}
                  className="mt-3 inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  {isGuardian && status.waitlistEntry?.studentName 
                    ? `Book for ${status.waitlistEntry.studentName}`
                    : t('course.bookNowAuthorized')}
                </button>
              </div>
            </div>
          </div>
        );

      case 'waitlisted':
        return (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-yellow-800">{t('course.status.waitlisted.title')}</p>
                {/* Show student name if it's a guardian booking */}
                {status.waitlistEntry?.studentName && (
                  <p className="text-xs text-yellow-700 mt-1">
                    Student: {status.waitlistEntry.studentName}
                  </p>
                )}
                <p className="text-xs text-yellow-600 mt-1">
                  {t('course.status.waitlisted.message')}
                </p>
              </div>
            </div>
          </div>
        );

      case 'booked':
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-800">✅ {t('course.status.booked.title')}</p>
                <p className="text-xs text-blue-600">{t('course.status.booked.message')}</p>
              </div>
            </div>
          </div>
        );

      case 'available':
        return (
          <button
            onClick={handleBookingClick}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('course.bookNow')}
          </button>
        );

      case 'full':
      case 'gender_full':
        return (
          <button
            onClick={handleBookingClick}
            className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('course.waitlist.joinButton')}
          </button>
        );

      default:
        return null;
    }
  };

  return (
    <div className="course-status-indicator">
      {renderStatusBadge()}
    </div>
  );
};

export default CourseStatusIndicator;